import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { listProveedores, crearCuentaPorPagar } from '../../services/data';
import { fechaCorta, hoyGT } from '../finanzas/estados';
import { fechaCalendario, sumarDiasCalendario } from '../../utils/dates';
import SucursalCampo from '../sucursales/SucursalCampo';
import MoneyInput from '../common/MoneyInput';
import '../finanzas/ui.css';
import FondoModal from '../common/FondoModal';

// Misma regla que el servidor (backend/lib/cuentas.js): fecha + días de crédito. Solo vista previa.
const sumarDias = (fecha, dias) => {
  return fechaCalendario(fecha) ? sumarDiasCalendario(fecha, Number(dias) || 0) : '';
};

// Registrar una compra/documento a crédito. No toca el efectivo: queda como cuenta por pagar.
export default function CompraCreditoModal({ sucursalId, proveedorIdInicial = '', onClose, onSaved }) {
  const [proveedores, setProveedores] = useState([]);
  const [f, setF] = useState({ proveedorId: proveedorIdInicial, tipo: 'compra_credito', referencia: '', fecha: hoyGT(), total: '', vencimiento: '' });
  const [editarVence, setEditarVence] = useState(false);
  const [sucursal, setSucursal] = useState(sucursalId === 'all' ? '' : sucursalId); // fija o a elegir con "todas"
  const [busy, setBusy] = useState(false);

  useEffect(() => { listProveedores(sucursalId).then((l) => setProveedores(l.filter((p) => p.activo))).catch(() => setProveedores([])); }, [sucursalId]);
  const prov = useMemo(() => proveedores.find((p) => p.id === f.proveedorId), [proveedores, f.proveedorId]);
  const venceCalculado = prov ? sumarDias(f.fecha, prov.diasCredito) : '';

  const guardar = async (e) => {
    e.preventDefault();
    if (!sucursal) return Swal.fire('Compra a crédito', 'Selecciona la sucursal.', 'warning');
    if (!f.proveedorId || !f.total || Number(f.total) <= 0) return Swal.fire('Compra a crédito', 'Selecciona el proveedor e ingresa el total.', 'warning');
    setBusy(true);
    try {
      await crearCuentaPorPagar({ sucursalId: sucursal, proveedorId: f.proveedorId, tipo: f.tipo, referencia: f.referencia, fecha: f.fecha, total: f.total,
        vencimiento: editarVence ? f.vencimiento : undefined });
      onSaved?.();
    } catch (err) { Swal.fire('Error', err.message || 'No se pudo guardar.', 'error'); }
    finally { setBusy(false); }
  };

  return (
    <FondoModal onClose={onClose}>
      <form className="fx-modal fx-modal--sm" role="dialog" aria-modal="true" aria-label="Registrar compra a crédito" onSubmit={guardar} onMouseDown={(e) => e.stopPropagation()}>
        <header><div><h2>Compra a crédito</h2><p>No descuenta efectivo: queda como saldo pendiente con el proveedor.</p></div><button type="button" aria-label="Cerrar" onClick={onClose}>×</button></header>
        <div className="fx-form">
          <SucursalCampo className="full" value={sucursal} onChange={setSucursal} />
          <label className="full">Proveedor<select value={f.proveedorId} onChange={(e) => setF({ ...f, proveedorId: e.target.value })} required>
            <option value="">— Selecciona proveedor —</option>
            {proveedores.map((p) => <option key={p.id} value={p.id}>{p.nombre} · {p.diasCredito} días</option>)}
          </select>{!proveedores.length && <small>No hay proveedores activos. Créalos en Proveedores.</small>}</label>
          <label>Tipo<select value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value })}>
            <option value="compra_credito">Compra a crédito</option><option value="factura_proveedor">Factura proveedor</option>
            <option value="credito_proveedor">Crédito proveedor</option><option value="otro">Otro pago pendiente</option></select></label>
          <label>Factura / documento<input value={f.referencia} onChange={(e) => setF({ ...f, referencia: e.target.value })} maxLength={120} /></label>
          <label>Fecha de compra<input type="date" value={f.fecha} onChange={(e) => setF({ ...f, fecha: e.target.value })} required /></label>
          <label>Total<MoneyInput value={f.total} onChange={(v) => setF({ ...f, total: v })} required /></label>
          <div className="full fx-card" style={{ gridColumn: '1/-1' }}>
            {editarVence ? (
              <label>Vencimiento del documento<input type="date" min={f.fecha} value={f.vencimiento} onChange={(e) => setF({ ...f, vencimiento: e.target.value })} required /></label>
            ) : (
              <div><b>Vence: {venceCalculado ? fechaCorta(venceCalculado) : '—'}</b>{prov && <small style={{ display: 'block', color: 'var(--ink-muted)' }}>{fechaCorta(f.fecha)} + {prov.diasCredito} días de crédito</small>}</div>
            )}
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontWeight: 400 }}>
              <input type="checkbox" checked={editarVence} onChange={(e) => { setEditarVence(e.target.checked); setF({ ...f, vencimiento: venceCalculado }); }} />
              El documento indica otra fecha de vencimiento
            </label>
          </div>
        </div>
        <div className="fx-actions"><button type="button" className="fx-btn" onClick={onClose}>Cancelar</button><button className="fx-btn fx-btn--primary" disabled={busy}>{busy ? 'Guardando…' : 'Guardar compra'}</button></div>
      </form>
    </FondoModal>
  );
}
